import { isEmptyObj } from "@dhx/ts-common/core";
import { create, el, awaitRedraw } from "@dhx/ts-common/dom";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { baseProps, getFormItemCss } from "../helper";
import { ValidationStatus, IRadioButtonConfig } from "../types";

export enum RadioButtonEvents {
	change = "change",
	focus = "focus",
	blur = "blur",
	keydown = "keydown",
}

export class RadioButton extends View {
	public config: IRadioButtonConfig;
	public events: IEventSystem<RadioButtonEvents>;
	protected _handlers: any;

	private _propsItem = ["text"];
	private _props = [...baseProps, ...this._propsItem];

	constructor(container: HTMLElement | string, config = {}) {
		super(container, {
			text: "",
			width: "content",
			height: "content",
			...config,
		});
		this._handlers = {
			onchange: (e: Event) => {
				this.config.checked = (e.target as HTMLInputElement).checked;
				this.events.fire(RadioButtonEvents.change, [(e.target as HTMLInputElement).checked]);
			},
			onfocus: () => this.events.fire(RadioButtonEvents.focus, [this.getValue() || "", this.config.id]),
			onblur: () => this.events?.fire(RadioButtonEvents.blur, [this.getValue() || "", this.config.id]),
			onkeydown: (event: KeyboardEvent) => {
				this.events.fire(RadioButtonEvents.keydown, [event, this.config.id]);
			},
		};
		this.events = new EventSystem();

		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	destructor(): void {
		this.events && this.events.clear();

		this.config = this.events = this._handlers = this._uid = this._props = this._propsItem = null;
		this.unmount();
	}
	setProperties(propertyConfig): void {
		if (!propertyConfig || isEmptyObj(propertyConfig)) return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this.paint();
	}
	getProperties() {
		const obj = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	getValue(): string | void {
		if (this.config.checked) {
			return this.config.value;
		}
	}
	setValue(checked: boolean): void {
		this.config.checked = checked;
		this.paint();
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.input.el.blur();
		});
	}
	disable(): void {
		this.config.$disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.$disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.$disabled;
	}
	clear(): void {
		this.config.checked = false;
		this.paint();
	}
	validate(): boolean {
		let isValid = false;
		if (this.config.checked) {
			isValid = true;
		}
		this.config.$validationStatus = isValid ? ValidationStatus.success : ValidationStatus.error;
		this.paint();
		return isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	private _draw() {
		const { id, value, checked, $disabled, $name, $required, text } = this.config;

		return el(
			"label.dhx_radiobutton.dhx_form-group",
			{
				class: getFormItemCss(this.config, !!$required),
			},
			[
				el("input.dhx_radiobutton__input", {
					type: "radio",
					id,
					value: value || "",
					name: $name || "",
					disabled: $disabled,
					checked,
					onchange: this._handlers.onchange,
					onfocus: this._handlers.onfocus,
					onblur: this._handlers.onblur,
					onkeydown: this._handlers.onkeydown,
					required: $required,
					_ref: "input",
				}),
				el("span.dhx_radiobutton__visual-input"),
				el("span.dhx_text", [text]),
			]
		);
	}
}
